import { Link } from 'react-router-dom';

export interface ProcessStep { number: string; title: string; desc: string; icon: string; }
export interface ProcessContent { badge?: string; heading: string; subheading?: string; steps: ProcessStep[]; }
interface Props { content: ProcessContent; }

export default function ProcessSection({ content }: Props) {
  return (
    <section className="w-full bg-white py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          <div className="lg:col-span-7">
            {content.badge && (
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-gold"></div>
                <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-gold">{content.badge}</span>
              </div>
            )}
            <h2 className="font-serif text-forest font-semibold leading-tight" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.2rem)' }}>
              {content.heading}
            </h2>
          </div>
          {content.subheading && (
            <div className="lg:col-span-5 flex items-end">
              <p className="font-sans text-charcoal-muted text-sm leading-relaxed">{content.subheading}</p>
            </div>
          )}
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {content.steps.map((step, i) => (
            <div key={i} className="relative flex flex-col">
              {/* Connector */}
              {i < content.steps.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-px bg-gradient-to-r from-gold/30 to-gold/5 z-0 pointer-events-none" style={{ width: 'calc(100% + 1.5rem)' }}></div>
              )}

              {/* Large decorative number */}
              <div className="relative mb-4 z-10">
                <span
                  className="font-serif font-bold text-gold/8 leading-none select-none"
                  style={{ fontSize: '6.5rem', lineHeight: '1' }}
                  aria-hidden="true"
                >
                  {step.number}
                </span>
                {/* Icon anchored on the number */}
                <div className="absolute bottom-2 left-0 w-12 h-12 flex items-center justify-center rounded-xl bg-forest text-white z-10 shadow-lg">
                  <i className={`${step.icon} text-lg`}></i>
                </div>
              </div>

              <h3 className="font-sans font-semibold text-forest text-sm mb-2 mt-1">{step.title}</h3>
              <p className="font-sans text-charcoal-muted text-xs leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-16 bg-[#f5f0e8] rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-serif text-forest text-xl font-semibold leading-tight">Start with a free on-site consultation.</p>
            <p className="font-sans text-charcoal-muted text-sm mt-1">No cost, no commitment — just an honest conversation about your project.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a href="tel:+12086596067" className="whitespace-nowrap inline-flex items-center gap-2 border border-forest/30 text-forest font-semibold text-sm rounded-full px-5 py-3 hover:bg-forest hover:text-white transition-all cursor-pointer">
              <i className="ri-phone-fill text-xs"></i> Call Now
            </a>
            <Link to="/#estimate" className="whitespace-nowrap inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm rounded-full px-6 py-3 transition-all cursor-pointer shadow-lg shadow-gold/20">
              Free Estimate <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
